import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuildingBanner = ({ token, user, onBuildingChange, compact = false }) => {
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [showBuildingSelector, setShowBuildingSelector] = useState(false);
  const [loading, setLoading] = useState(true);

  const isCoproprietaire = user?.role === 'coproprietaire';

  // Fetch buildings for the coproprietaire
  useEffect(() => {
    if (!isCoproprietaire || !token) {
      setLoading(false);
      return;
    }
    
    const fetchBuildings = async () => {
      try {
        const response = await axios.get(`${API}/immeubles`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setBuildings(response.data);
        // Set first building as selected by default
        if (response.data.length > 0 && !selectedBuildingId) {
          setSelectedBuildingId(response.data[0].id);
          if (onBuildingChange) {
            onBuildingChange(response.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching buildings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBuildings();
  }, [token, isCoproprietaire]);

  const handleBuildingChange = (buildingId) => {
    setSelectedBuildingId(buildingId);
    setShowBuildingSelector(false);
    if (onBuildingChange) {
      onBuildingChange(buildingId);
    }
  };

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  // Only show for coproprietaires
  if (!isCoproprietaire || loading || buildings.length === 0) {
    return null;
  }

  // Compact version for sub-pages
  if (compact) {
    return (
      <div className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] rounded-xl shadow-sm p-3 text-white mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Building2 size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {selectedBuilding?.nom || 'Chargement...'}
              </div>
              <div className="text-xs text-white/80 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span>{selectedBuilding?.adresse}</span>
                {selectedBuilding?.code_postal && selectedBuilding?.ville && (
                  <span>• {selectedBuilding.code_postal} {selectedBuilding.ville}</span>
                )}
                {selectedBuilding?.nb_lots && (
                  <span>• {selectedBuilding.nb_lots} lots</span>
                )}
                {selectedBuilding?.total_tantiemes && (
                  <span>• {selectedBuilding.total_tantiemes.toLocaleString()} tantièmes</span>
                )}
                {selectedBuilding?.immatriculation && (
                  <span>• Immat. {selectedBuilding.immatriculation}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Selector for multiple buildings */}
          {buildings.length > 1 && (
            <div className="relative flex-shrink-0 ml-2">
              <button
                onClick={() => setShowBuildingSelector(!showBuildingSelector)}
                className="flex items-center space-x-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors text-sm"
                data-testid="building-selector-btn-compact"
              >
                <span className="font-medium">Changer</span>
                <ChevronDown size={14} className={`transition-transform ${showBuildingSelector ? 'rotate-180' : ''}`} />
              </button>
              
              {showBuildingSelector && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="p-2 bg-gray-50 border-b">
                    <span className="text-xs font-medium text-gray-500 uppercase">Mes résidences ({buildings.length})</span>
                  </div>
                  {buildings.map((building) => (
                    <button
                      key={building.id}
                      onClick={() => handleBuildingChange(building.id)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center space-x-2 ${
                        selectedBuildingId === building.id ? 'bg-[#00D3A8]/10' : ''
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        selectedBuildingId === building.id ? 'bg-[#00D3A8] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Building2 size={12} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-xs truncate ${
                          selectedBuildingId === building.id ? 'text-[#00D3A8]' : 'text-gray-800'
                        }`}>
                          {building.nom}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{building.adresse}</div>
                      </div>
                      {selectedBuildingId === building.id && (
                        <span className="text-[#00D3A8] text-sm">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full version for dashboard
  return (
    <div className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] rounded-xl shadow-sm p-4 text-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Building2 size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-white/80 uppercase tracking-wide font-medium">
              {buildings.length > 1 ? 'Ma résidence sélectionnée' : 'Ma résidence'}
            </div>
            <div className="font-semibold text-lg">
              {selectedBuilding?.nom || 'Chargement...'}
            </div>
            <div className="text-sm text-white/90 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span>{selectedBuilding?.adresse}</span>
              {selectedBuilding?.code_postal && selectedBuilding?.ville && (
                <span>• {selectedBuilding.code_postal} {selectedBuilding.ville}</span>
              )}
              {selectedBuilding?.nb_lots && (
                <span>• {selectedBuilding.nb_lots} lots</span>
              )}
              {selectedBuilding?.total_tantiemes && (
                <span>• {selectedBuilding.total_tantiemes.toLocaleString()} tantièmes</span>
              )}
              {selectedBuilding?.immatriculation && (
                <span>• Immat. {selectedBuilding.immatriculation}</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Selector for multiple buildings */}
        {buildings.length > 1 && (
          <div className="relative flex-shrink-0 ml-3">
            <button
              onClick={() => setShowBuildingSelector(!showBuildingSelector)}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
              data-testid="building-selector-btn"
            >
              <span className="text-sm font-medium">Changer</span>
              <ChevronDown size={16} className={`transition-transform ${showBuildingSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {showBuildingSelector && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="p-2 bg-gray-50 border-b">
                  <span className="text-xs font-medium text-gray-500 uppercase">Mes résidences ({buildings.length})</span>
                </div>
                {buildings.map((building) => (
                  <button
                    key={building.id}
                    onClick={() => handleBuildingChange(building.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center space-x-3 ${
                      selectedBuildingId === building.id ? 'bg-[#00D3A8]/10' : ''
                    }`}
                    data-testid={`building-option-${building.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      selectedBuildingId === building.id ? 'bg-[#00D3A8] text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Building2 size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm truncate ${
                        selectedBuildingId === building.id ? 'text-[#00D3A8]' : 'text-gray-800'
                      }`}>
                        {building.nom}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{building.adresse}</div>
                    </div>
                    {selectedBuildingId === building.id && (
                      <span className="text-[#00D3A8]">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Lot info if available */}
      {user?.lot && (
        <div className="mt-3 pt-3 border-t border-white/20 flex items-center space-x-4 text-sm">
          <div>
            <span className="text-white/70">Lot :</span> <span className="font-medium">{user.lot}</span>
          </div>
          {user?.tantiemes && (
            <div>
              <span className="text-white/70">Tantièmes :</span> <span className="font-medium">{user.tantiemes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BuildingBanner;
